import { Component, ElementRef, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { SimulatorService } from '../services/simulator.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { startOfDay, endOfDay, addDays } from 'date-fns';
import { BookingService } from '../services/booking.service';
import { BookingSchedule } from '../models/booking-schedule';
import { RecurrenceEditor, ScheduleComponent } from '@syncfusion/ej2-angular-schedule';
import { BaseBookingSchedulerV2Component } from './base-booking-scheduler-v2.component';
import { MatDialog } from '@angular/material/dialog';
import { AccountService } from 'src/app/account/account.service';
import { BookingEventEmitters } from '../booking-extensions/services/booking-event-emitters';
import { PromoCodeDiscountType } from 'src/app/shared/models/promo-code-discount-type.enum';
import { BookingCreateModalComponent } from '../modals/booking/booking-create-modal/booking-create-modal.component';
import { CreateScheduledBookingModalComponent } from '../modals/booking/create-scheduled-booking-modal/create-scheduled-booking-modal.component';
import { EditScheduledBookingModalComponent } from '../modals/booking/edit-scheduled-booking-modal/edit-scheduled-booking-modal.component';
import { CalendarComponent } from '@syncfusion/ej2-angular-calendars';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { BookingSchedulerQueryFilter } from './booking-scheduler-query-filter';
import { ScheduledBooking } from '../models/scheduled-booking';
import { BookingStatusEnum } from 'src/app/shared/models/booking-status.enum';

@Component({
  selector: 'booking-scheduler-v2',
  templateUrl: './booking-scheduler-v2.component.html',
  styleUrls: ['./booking-scheduler-v2.component.scss'],
  standalone: false,
})
export class BookingSchedulerV2Component extends BaseBookingSchedulerV2Component implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('schedulerContainer') schedulerContainer: ElementRef;
  @ViewChild('scheduleObj') declare scheduleObj: ScheduleComponent;
  @ViewChild('calendar') calendar: CalendarComponent;
  @ViewChild('recurrenceObj') recurrenceObj!: RecurrenceEditor;
  
  calendarOpen = false;
  isMobile = false;
  simulators: any[] = [];
  filter = new BookingSchedulerQueryFilter();
  bookings: BookingSchedule[] = [];
  
  private selectedCellInfo: {
    startTime: Date;
    endTime: Date;
    simulatorId: string;
  } | null = null;

  private bookingCreateSubscription: Subscription;
  private bookingUpdateSubscription: Subscription;
  private scheduledBookingUpdateSubscription: Subscription;
  private scheduledBookingCreateSubscription: Subscription;
  
  constructor(private simulatorService: SimulatorService,
    protected bookingService: BookingService,
    sessionService: SessionService,
    stringHelpers: StringHelpers,
    protected bookingEventEmitters: BookingEventEmitters,
    protected accountService: AccountService,
    protected dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private signalrService: SignalrService) {
    super(sessionService, bookingEventEmitters, stringHelpers, accountService, dialog, bookingService);
  }

  ngOnInit(): void {
    this.bookingEventEmitters.updateBookingEvent.subscribe(() => {
      this.getBookingsForAllSimulators();
    });

    this.bookingEventEmitters.cancelBookingEvent.subscribe(() => {
      this.getBookingsForAllSimulators();
    });

    this.bookingEventEmitters.dateChangeDateEvent.subscribe(() => {
      this.getBookingsForAllSimulators();
    });

    this.bookingEventEmitters.updateScheduledBookingEvent.subscribe(() => {
      this.getBookingsForAllSimulators();
    });

    this.bookingEventEmitters.baseFinishedInitializingEvent.subscribe(() => {
      this.getSimulators();
    });

    this.bookingEventEmitters.updateScheduledBookingEvent.subscribe(() => {
      this.getBookingsForAllSimulators();
    });

    this.initializeSignalR();
  }

  ngAfterViewInit() {
    setTimeout(() => this.updateToolbarState(), 0);
  }

  ngOnDestroy(): void {
    if (this.bookingCreateSubscription) {
      this.bookingCreateSubscription.unsubscribe();
    }

    if (this.bookingUpdateSubscription) {
      this.bookingUpdateSubscription.unsubscribe();
    }

    if (this.scheduledBookingUpdateSubscription) {
      this.scheduledBookingUpdateSubscription.unsubscribe();
    }

    if (this.scheduledBookingCreateSubscription) {
      this.scheduledBookingCreateSubscription.unsubscribe();
    }
  }

  private initializeSignalR(): void {
    if (this.sessionService.isUserLoggedIn()) {
      this.signalrService.startConnection(environment.signalR.bookingHubUrl, this.sessionService.getUser().auth0Id);
      this.waitForConnectionAndSetupListeners();
    }
  }

  private waitForConnectionAndSetupListeners(): void {
    const checkConnection = () => {
      if (this.signalrService.isConnected()) {
        this.setupBookingListeners();
      } else {
        setTimeout(checkConnection, 100);
      }
    };
    
    setTimeout(checkConnection, 100);
  }

  private setupBookingListeners(): void {
    this.bookingCreateSubscription = this.signalrService.getListnerConnection(environment.signalR.bookingCreateListener)
      .subscribe((result: BookingSchedule) => {
        this.getBookingsForAllSimulators();
      });

    this.bookingUpdateSubscription = this.signalrService.getListnerConnection(environment.signalR.bookingUpdateListener)
      .subscribe((result: BookingSchedule) => {
        this.getBookingsForAllSimulators();
      });

    this.scheduledBookingUpdateSubscription = this.signalrService.getListnerConnection(environment.signalR.scheduledBookingUpdateListener)
      .subscribe((result: ScheduledBooking) => {
        this.getBookingsForAllSimulators();
      });

    this.scheduledBookingCreateSubscription = this.signalrService.getListnerConnection(environment.signalR.scheduledBookingCreateListener)
      .subscribe((result: ScheduledBooking) => {
        this.getBookingsForAllSimulators();
      });
  }

  updateToolbarState() {
    if (this.scheduleObj && this.scheduleObj.element) {
      const prevBtn = this.scheduleObj.element.querySelector('.e-toolbar-item[aria-label="Previous"], .e-toolbar-item[title="Previous"]');
      if (prevBtn) {
        if (this.isPreviousDayDisabled()) {
          prevBtn.classList.add('e-overlay');
          prevBtn.setAttribute('aria-disabled', 'true');
        } else {
          prevBtn.classList.remove('e-overlay');
          prevBtn.removeAttribute('aria-disabled');
        }
      }
    }
  }

  onNextDayClick(args) {
    this.selectedDate = new Date(this.selectedDate.setDate(this.selectedDate.getDate() + 1));
    this.updateToolbarState();
  }

  onScheduleDataBound(args) {
    this.updateToolbarState();
  }

  getSimulators() {
    this.isLoading = true;
    this.simulatorService.getAllByOrganization({
      organizationId: this.sessionService.getUser().organizationId
    }).subscribe({
      next: (result) => {
        this.simulators = result.results
          .filter(x => x.isAvailable === true)
          .map(simulator => ({
            simulatorTitle: `Bay ${simulator.bayNumber} (${simulator.bayName})`,
            simulatorColor: '#4d77b6',
            simulatorId: simulator.id,
            id: simulator.id,
            bayNumber: simulator.bayNumber,
            bayName: simulator.bayName,
            capacity: simulator.capacity,
            createdBy: simulator.createdBy,
            createdOn: simulator.createdOn,
            isAvailable: simulator.isAvailable,
            isDeleted: simulator.isDeleted,
            lastEditedBy: simulator.lastEditedBy,
            lastEditedDate: simulator.lastEditedDate,
            organizationId: simulator.organizationId,
          }));
        this.getBookingsForAllSimulators();
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      }
    });
  }

  getBookingsForAllSimulators() {
    this.isLoading = true;
    this.filter.organizationId = this.sessionService.getUser().organizationId;
    this.filter.startDate = startOfDay(addDays(this.selectedDate, -10)).toDateString();
    this.filter.endDate = endOfDay(addDays(this.selectedDate, 7)).toDateString();
    this.filter.timeZone = this.userDetails.timeZone;

    let completed = 0;
    const total = this.simulators.length;
    const allEvents: any[] = [];

    this.bookings = [];
    this.simulators.forEach(simulator => {
      this.filter.simulatorId = simulator.id;
      this.bookingService.getAllForScheduler(this.filter).subscribe({
        next: (bookings) => {
          this.bookings.push(...bookings);
          const events = this.buildBookingDisplayForSimulator(bookings, simulator.id);
          allEvents.push(...events);
        },
        error: (error) => {
          console.error(error);
        },
        complete: () => {
          completed++;
          if (completed === total) {
            this.eventSettings = {
              ...this.eventSettings,
              dataSource: allEvents
            };
            if (this.scheduleObj) {
              this.scheduleObj.workHours = this.workHours;
              this.scheduleObj.eventSettings = this.eventSettings;
              this.scheduleObj.dataBind();
              this.scheduleObj.refresh();
            }
            this.isLoading = false;
          }
        }
      });
    });
  }

  buildBookingDisplayForSimulator(bookings: BookingSchedule[], simulatorId: string): any[] {
    const events: any[] = [];

    bookings.forEach(x => {
      let content = `Confirmation #${x.confirmationNumber}`;
      if (x.priceOverride !== null && x.priceOverride > 0) {
        content += `\n Price Override $${x.priceOverride.toFixed(2)} \n ${x.priceOverrideReason}`;
      }

      content += `\n ${x.participants} golfer(s) \n ${x.email}`;

      let title = `${x.customerName}`;


      if (x.status === BookingStatusEnum.Scheduled) {
        title += ` (Scheduled)`;
      } else if (x.hasPaid) {
        title += ` (Paid $${x.totalPrice.toFixed(2)})`;
        if (x.promoCodeId) {
          if (x.discountType === PromoCodeDiscountType.Percentage) {
            content += `\n(Promo Code Applied: ${x.code} ${x.discountAmount.toFixed(2)}% off)`;
          } else {
            content += `\n(Promo Code Applied: ${x.code} $${x.discountAmount.toFixed(2)} off)`;
          }
        }
      } else {
        title += ` (Unpaid $${x.totalPrice.toFixed(2)})`;
      }

      if (x.addons.length > 0) {
        content += `\n Addons: ${x.addons.map(addon => addon.name).join(', ')}`;
      }

      let status = '';
      if(this.isBookingInProgress({start: x.startTime, end: x.endTime})) {
        if(x.membershipCustomerId) {
          status = 'Member/In Progress';
        } else if(x.status === BookingStatusEnum.Scheduled) {
          status = 'Scheduled/In Progress';
        } else {
          status = 'In Progress';
        }
      } else if(this.isBookingPast(new Date(x.endTime))) {
        status = 'Past';
      }else if(x.membershipCustomerId !== null && x.membershipCustomerId !== undefined) {
        status = 'Member/Upcoming';
      } else if(x.status === BookingStatusEnum.Scheduled) {
        status = 'Scheduled/Upcoming';
      } else {
        status = 'Upcoming';
      }

      events.push({
        Id: x.id,
        Subject: title,
        StartTime: new Date(x.startTime),
        EndTime: new Date(x.endTime),
        IsAllDay: false,
        Description: content,
        SimulatorId: simulatorId,
        Status: status,
        PaymentStatus: x.hasPaid ? 'Paid' : 'Unpaid',
        ScheduledBookingId: x.scheduledBookingId,
      });
    });

    return events;
  }

  onPopupOpen($event) {
    if ($event.data?.Id === undefined && $event.type.toLowerCase() === 'quickinfo' || $event.type.toLowerCase() === 'editor') {
      $event.cancel = true;
      this.createBooking($event.data);
    }
  }

  onEventDoubleClick(args) {
    args.cancel = true;
    let booking = this.bookings.find(x => x.id === args.event.Id);
    this.editBooking(booking, this.simulators);
  }

  onEventEdit(args, isScheduledBooking = false) {
    this.scheduleObj.closeQuickInfoPopup();
    if(isScheduledBooking) {
      this.editScheduledBooking(args.ScheduledBookingId);
    } else {
      this.editBooking(this.bookings.find(x => x.id === args.Id), this.simulators);
    }
  }

  editScheduledBooking(scheduledBookingId: string) {
    const dialogRef = this.dialog.open(EditScheduledBookingModalComponent, {
      width: '70vw',
      height: '90vh',
      disableClose: true
    });

    dialogRef.componentInstance.id = scheduledBookingId;
    dialogRef.componentInstance.simulators = this.simulators;
    
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'save') {
        this.bookingEventEmitters.updateScheduledBookingEvent.emit();
      }
    });
  }

  onEventCancel(args) {
    this.cancelBooking(args,this.bookings.find(x => x.id === args.Id));
  }


  onEventDrag(args) {
    let newStartTime = args.data.StartTime;
    let newEndTime = args.data.EndTime;
    let booking = this.bookings.find(x => x.id === args.data.Id);
    let oldStartTime = booking?.startTime;
    let slotAvailable = this.scheduleObj.isSlotAvailable(newStartTime, newEndTime);

    if(!slotAvailable) {

      if (newStartTime.getTime() < Date.now() && new Date(oldStartTime).getTime() > Date.now()) {
        this.alert(
          "Invaild Booking Date",
          "Unable to move booking. The time window you are trying to select is a previous time slot that has already passed. Please select a different time slot.",
          "Okay"
        );
        args.cancel = true;
        return;
      }
  
      if (new Date(oldStartTime).getTime() <= Date.now()) {
        this.alert(
          "Invaild Booking Date",
          "Cannot reschedule a past booking or booking already in progress. Please click the edit icon if you need to move this booking or the delete icon to cancel or permanently delete this booking.",
          "Okay"
        )
        args.cancel = true;
        return;
      }

      this.scheduleObj.openOverlapAlert(args);
      args.cancel = true;
      return;
    }

    this.isLoading = true;

    if (booking) {
      booking.startTime = newStartTime;
      booking.endTime = newEndTime;
      booking.lastEditedBy = this.userDetails.id;
      booking.simulatorId = args.data.SimulatorId;

      let data: any = booking;
      data.timeZone = this.userDetails.timeZone;
      data.startTime = this.stringHelpers.normalizeDateTime(data.startTime);
      data.endTime = this.stringHelpers.normalizeDateTime(data.endTime);

      this.bookingService.update(booking.id, data).subscribe({
        next: () => {
          this.getBookingsForAllSimulators();
        },
        error: (error) => {
          this.isLoading = false;
          args.cancel = true;
          console.error(error);
        },
        complete: () => this.isLoading = false
      });
    }
  }

  createBooking(data: any) {
    if (this.simulators.length > 0) {
      const dialogRef = this.dialog.open(BookingCreateModalComponent, {
        width: '75vw',
        height: 'auto',
        disableClose: true,
      });

      dialogRef.componentInstance.startTime = data.StartTime;
      dialogRef.componentInstance.simulator = this.simulators.find(x => x.id == data.SimulatorId);
      dialogRef.componentInstance.form.get('simulatorId').setValue(data.SimulatorId);
      dialogRef.componentInstance.userDetails = this.userDetails;

      const startTime = new Date(data.StartTime).getTime();
      const endTime = new Date(data.EndTime).getTime();
      const diffHours = (endTime - startTime) / (1000 * 60 * 60);
      if (diffHours > 0.5) {
        dialogRef.componentInstance.form.get('endTime').setValue(data.EndTime);
        dialogRef.componentInstance.setEndTimeAndTotalPrice({ duration: diffHours, price: 0 });
      }

      dialogRef.afterClosed().subscribe(result => {
        if (result === "save") {
          //this.getBookingsForAllSimulators();
        }
      });
    } else {
      this.alert("No Simulators Available", "No simulators are available for booking. Please add a simulator to use the scheduler.", "Okay");
    }
  }

  openCalendar() {
    this.calendar.element.style.display = 'visible';
    let calendar = document.getElementById('calendar');
    calendar.style.display = 'block';
  }

  toggleCalendar() {
    this.calendarOpen = !this.calendarOpen;
  }

  closeCalendar() {
    this.calendarOpen = false;
  }

  changeSelectedDate(args) {
    this.selectedDate = args > 0 ? new Date(this.selectedDate.setDate(this.selectedDate.getDate() + args)) : new Date(this.selectedDate.setDate(this.selectedDate.getDate() - Math.abs((args))));
    this.setOperatingHours(this.selectedDate);
    this.getBookingsForAllSimulators();
  }

  onCellClick(args: any) {
    if (this.isMobileOrTablet()) {
      this.selectedCellInfo = {
        startTime: args.startTime,
        endTime: args.endTime,
        simulatorId: args.groupIndex !== undefined ? this.simulators[args.groupIndex].id : this.simulators[0].id
      };
    }else{
      args.cancel = true;
    }
  }

  createScheduledBooking() {
    if (this.simulators.length > 0) {
        const recurrenceData: ScheduledBooking = {
        id: '',
        organizationId: this.sessionService.getUser().organizationId,
        lastEditedBy: this.userDetails.id,
        startTime: this.selectedDate,
        endTime: new Date(this.selectedDate.getTime() + (60 * 60 * 1000)),
        simulatorIds: [], 
        type: null,
        name: '',
        price: null,
      };

      const dialogRef = this.dialog.open(CreateScheduledBookingModalComponent, {
        width: '70vw',
        height: '80vh',
        disableClose: true,
        data: recurrenceData
      });

      dialogRef.componentInstance.simulators = this.simulators;

      dialogRef.afterClosed().subscribe(result => {
        if (result && result.action === 'save') {
          this.getBookingsForAllSimulators();
        }
      });
    } else {
      this.alert("No Simulators Available", "No simulators are available for booking. Please add a simulator to use the scheduler.", "Okay");
    }
  }
  
  private isMobileOrTablet(): boolean {
    this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
    ]).subscribe(result => {
      this.isMobile = result.matches;
    });
    return this.isMobile;
  }

}
